import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router";

import type { LanguageDto } from "@/api/types";
import { AutoTranslationLanguageSelector } from "@/components/AutoTranslation/AutoTranslationLanguageSelector";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { getBaseLanguageCode } from "@/components/ProductUpdates/supportedLanguagePrompt";
import { Redirect } from "@/components/Routing/Redirect";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { updateLocalStorage } from "@/hooks/useLocalStorage";
import { useSlug } from "@/hooks/useSlug";
import { OnboardingFlowCard } from "@/modules/onboarding-flow/components/OnboardingFlowCard";
import { OnboardingFlowActions, OnboardingFlowRoot } from "@/modules/onboarding-flow/components/OnboardingFlowLayout";
import { OnboardingTopBar } from "@/modules/onboarding-flow/components/OnboardingTopBar";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function OnboardingAutoTranslationSettingPage(): React.ReactNode {
  const sessionUser = useSessionUser();
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageDto | null>(
    sessionUser.autoTranslationLanguage ?? null,
  );

  const { t } = useTranslation();
  useDocumentTitle(t("page.onboarding.auto-translation-setting.title"));
  const slug = useSlug();
  const navigate = useNavigate();

  const generalQueries = useGeneralQueries();
  const {
    data: languages,
    isPending: isPendingLanguages,
    isError: isErrorLanguages,
  } = useQuery(generalQueries.languages({ subset: "autoTranslation" }));
  const updateSelfLanguageMutation = selfAccountMutations.useUpdateLanguage();

  const handleSelectLanguage = (language: LanguageDto | null) => {
    setSelectedLanguage(language);
  };

  if (isPendingLanguages) {
    return <FullSizeLoader />;
  }

  if (isErrorLanguages) {
    return <Redirect to={({ slug: currSlug }) => routes.onboardingFlow.codeOfConduct({ slug: currSlug })} />;
  }

  const handleClickContinue = () => {
    updateLocalStorage(LOCAL_STORAGE_KEYS.NEW_FEATURE.AUTO_TRANSLATION_FEATURE, "true");
    const browserLanguageId = getBaseLanguageCode(window.navigator.language);
    if (languages?.some((language) => language.id === browserLanguageId)) {
      updateLocalStorage(LOCAL_STORAGE_KEYS.NEW_FEATURE.AUTO_TRANSLATION_USER_LANGUAGE_SUPPORTED, browserLanguageId);
    }

    updateSelfLanguageMutation.mutate(
      {
        uiLanguageId: sessionUser.uiLanguage.id,
        autoTranslationLanguageId: selectedLanguage?.id,
      },
      {
        onSettled: () => {
          void navigate(routes.onboardingFlow.groups({ slug }));
        },
      },
    );
  };

  return (
    <OnboardingFlowRoot>
      <OnboardingTopBar />
      <OnboardingFlowCard>
        <Stack gap="sm">
          <Translate01Icon size={40} />
          <h3 className="text-heading-3">{t("page.onboarding.auto-translation-setting.title")}</h3>
        </Stack>
        <MultiLineContent>{t("page.onboarding.auto-translation-setting.body")}</MultiLineContent>
        <AutoTranslationLanguageSelector
          languages={languages}
          onSelectLanguage={handleSelectLanguage}
          selectedLanguage={selectedLanguage}
        />
        <OnboardingFlowActions>
          <Stack gap="sm">
            <Button
              fullWidth
              onClick={handleClickContinue}
              isLoading={updateSelfLanguageMutation.isPending}
              data-testid="next-step-button"
            >
              {t("common.action.next")}
            </Button>
            <Button
              type="link"
              variant="soft"
              href={routes.onboardingFlow.userProfile({
                slug,
              })}
              fullWidth
              disabled={updateSelfLanguageMutation.isPending}
            >
              {t("common.action.back")}
            </Button>
          </Stack>
        </OnboardingFlowActions>
      </OnboardingFlowCard>
    </OnboardingFlowRoot>
  );
}
